/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { forceLoginRedirect } from "../utils/force-redirect"

export interface ApiErrorResponse {
    status: number
    responseObject: Record<string, unknown>
}

const enum Method {
    GET = "GET",
    PUT = "PUT",
    POST = "POST",
    DELETE = "DELETE",
    OPTIONS = "OPTIONS"
}

type RequestParams = Record<string, string> | null
export type ResponseType = "json" | "text" | "blob"

type SuccessResult<T> = {
    success: true
    data: T | null
}

type ErrorResult = {
    success: false
    error?: Error
    apiError?: ApiErrorResponse
}

export enum AuthError {
    TOTP_REQUIRED = "TOTP_REQUIRED",
    INVALID_CREDENTIALS = "INVALID_CREDENTIALS"
}

export type Result<T> = SuccessResult<T> | ErrorResult

// Separate error and apiError as soon as possible, so we do not have to do it further down the road.
export const makePutRequest = async <Req, Res>(
    path: string,
    params: RequestParams,
    payload?: Req
): Promise<Result<Res>> => {
    return makeRequest<Req, Res>(Method.PUT, path, params, payload)
}

export const makePostRequest = async <Req, Res>(
    path: string,
    params: RequestParams,
    payload?: Req,
    responseType: ResponseType = "json"
): Promise<Result<Res>> => {
    return makeRequest<Req, Res>(Method.POST, path, params, payload, responseType)
}

export const makeGetRequest = async <Res>(path: string, params: RequestParams): Promise<Result<Res>> => {
    return makeRequest<unknown, Res>(Method.GET, path, params)
}

export const makeOptionsRequest = async <Res>(path: string, params: RequestParams): Promise<Result<Res>> => {
    return makeRequest<unknown, Res>(Method.OPTIONS, path, params)
}

export const makeDeleteRequest = async <Res>(path: string, params: RequestParams = null): Promise<Result<Res>> => {
    return makeRequest<unknown, Res>(Method.DELETE, path, params)
}

// For API calls
const makeRequest = async <Req, Res>(
    method: Method,
    path: string,
    params: RequestParams = null,
    payload: Req | null = null,
    responseType: ResponseType = "json"
): Promise<Result<Res>> => {
    try {
        const searchParams = params ? new URLSearchParams(params) : ""
        const requestInit: RequestInit = buildRequestConfig(method, payload, responseType)

        const url = `${getBaseUrl()}${path}${searchParams.toString() ? `?${searchParams}` : ""}`
        const response = await fetch(url, requestInit)

        // Happy path
        if (response.ok) {
            // empty payloads
            if (response.status === 204) {
                // No-Content
                return { success: true, data: null }
            }

            let data: Res | null

            switch (responseType) {
                case "blob":
                    data = (await response.blob()) as Res
                    break
                case "text":
                    data = (await response.text()) as Res
                    break
                default: {
                    const text = await response.text()
                    data = text.trim() ? JSON.parse(text) : null
                    break
                }
            }

            return { success: true, data }
        }
        // Handle API errors
        if (!response.ok) {
            const authError = checkCustomAuthHeader(response, path)
            if (authError) {
                return {
                    success: false,
                    error: authError
                }
            }
            handleNotOkResponse(response.status)

            // First we get the text from the body
            const errorResponse = await parseErrorResponse(response)

            return {
                success: false,
                apiError: {
                    status: (errorResponse as ApiErrorResponse)?.status ?? response.status,
                    responseObject: (errorResponse as ApiErrorResponse)?.responseObject ?? errorResponse
                }
            }
        }

        // Empty API response
        const err = new Error("API response status not found")
        return {
            success: false,
            error: err
        }
    } catch (error: unknown) {
        // Handle non-API errors
        const err = error instanceof Error ? error : new Error(`Request failed: ${error}`)
        return {
            success: false,
            error: err
        }
    }
}

function getBaseUrl() {
    const baseApiUrl = process.env.API_URL
    if (!baseApiUrl) {
        throw new Error("API URL is missing in ENV variable")
    }
    return baseApiUrl
}

function buildRequestConfig<Req>(method: Method, payload: Req | null, responseType: ResponseType): RequestInit {
    let headers: HeadersInit | undefined
    let body: BodyInit | undefined

    if (!payload) {
        headers = getJSONHeaders(responseType)
    } else if (payload instanceof FormData) {
        body = payload
    } else if (payload instanceof URLSearchParams) {
        headers = getURLEncodedHeaders()
        body = payload.toString()
    } else {
        headers = getJSONHeaders(responseType)
        body = JSON.stringify(payload)
    }

    return {
        method,
        credentials: "include",
        headers: {
            ...headers,
            ...xsrfTokenHeader(method)
        },
        body
    }
}

function xsrfTokenHeader(method: Method): HeadersInit {
    // XSRF-TOKEN is always sent with requests that modify data
    switch (method) {
        case Method.POST:
        case Method.PUT:
        case Method.DELETE: {
            const token = getCookieValue("XSRF-TOKEN")
            return token ? { "X-XSRF-TOKEN": token } : {}
        }
        default:
            return {}
    }
}

/**
 * Gets the value of a cookie by name
 * @param name The name of the cookie to retrieve
 * @returns The cookie value or empty string if not found
 */
function getCookieValue(name: string): string {
    const pattern = new RegExp(`(?:^|;\\s*)${name}=([^;]*)`)
    const match = document.cookie.match(pattern)
    if (match) {
        return decodeURIComponent(match[1])
    }
    return ""
}

function getURLEncodedHeaders(): HeadersInit {
    return { "Content-Type": "application/x-www-form-urlencoded" }
}

function getJSONHeaders(responseType: ResponseType): HeadersInit {
    return {
        "Content-Type": "application/json",
        Accept: responseType === "blob" ? "image/png" : "*/*",
        "Accept-Encoding": "gzip, deflate, br"
    }
}

async function parseErrorResponse(response: Response): Promise<ApiErrorResponse | string> {
    const text = await response.text()
    if (!text.trim()) return ""

    try {
        return JSON.parse(text)
    } catch {
        if (process.env.NODE_ENV === "development") {
            console.debug("Unstructured API error body received")
        }
        return text
    }
}

function checkCustomAuthHeader(response: Response, path: string): Error | null {
    if (path !== "/login") return null

    const header = response.headers.get("WWW-Authenticate")?.toLowerCase()
    if (header === "totp") return new Error(AuthError.TOTP_REQUIRED)
    if (response.status === 401) return new Error(AuthError.INVALID_CREDENTIALS)

    return null
}

function handleNotOkResponse(statusCode: number) {
    if (statusCode === 401) {
        // Unauthorized alert will be visible, redirect to login page
        forceLoginRedirect()
    }
}
